import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { inventoryActions } from "../../sagas/inventorySlice";
import { useFormik } from "formik";
import {
    Container, Card, CardBody, Row, Col, Button, Table, Input
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import stockService from "../../services/stockService";
import ProductSelectModal from "./ProductSelectModal";
import { productActions } from "../../sagas/productSlice";
import { FaArrowLeft, FaClipboardCheck } from "react-icons/fa";
import { entrepotActions } from "../../sagas/entrepotSlice";
import Select from "react-select";
import { preventScroll } from "../../helpers/utility";

const InventoryFormPage = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { currentInventory } = useSelector(state => state.inventory);
    const { entrepots } = useSelector(state => state.entrepots);

    const products = useSelector(state => state.products.products);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalProducts, setModalProducts] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [lineSearchText, setLineSearchText] = useState("");
    const [linePage, setLinePage] = useState(0);
    const [linePageSize, setLinePageSize] = useState(10);
    const [duplicateArticleId, setDuplicateArticleId] = useState(null);
    const [highlightedArticleId, setHighlightedArticleId] = useState(null);

    useEffect(() => {
        if (id) {
            dispatch(inventoryActions.getInventory(id));
        } else {
            dispatch(inventoryActions.clearCurrentInventory()); // 🔥 important
        }
    }, [id]);

    useEffect(() => {
        dispatch(entrepotActions.list());
    }, []);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: currentInventory || {
            dateInventaire: new Date().toISOString().split("T")[0], // 📅 today
            entrepotId: "",
            lignes: []
        },
        onSubmit: (values) => {
            if (id) {
                dispatch(inventoryActions.updateInventory({
                    id,
                    data: values,
                    onSuccess: () => navigate("/inventories")
                }));
            } else {
                dispatch(inventoryActions.createInventory({
                    data: values,
                    onSuccess: () => navigate("/inventories")
                }));
            }
        }
    });

    const isLocked = currentInventory?.status === "VALIDE";
    const lines = formik.values.lignes || [];
    const normalizedLineSearch = lineSearchText.trim().toLowerCase();

    const visibleLines = useMemo(() => {
        return lines
            .map((line, index) => ({ line, index }))
            .filter(({ line }) => {
                if (!normalizedLineSearch) return true;

                const articleText = `${line.articleCode || ""} ${line.articleLibelle || ""}`.toLowerCase();
                return articleText.includes(normalizedLineSearch);
            })
            .sort((a, b) => {
                const aPriority = a.line.articleId === duplicateArticleId || a.line.articleId === highlightedArticleId ? 0 : 1;
                const bPriority = b.line.articleId === duplicateArticleId || b.line.articleId === highlightedArticleId ? 0 : 1;

                if (aPriority !== bPriority) return aPriority - bPriority;
                return a.index - b.index;
            });
    }, [lines, normalizedLineSearch, duplicateArticleId, highlightedArticleId]);

    const totalLinePages = Math.max(1, Math.ceil(visibleLines.length / linePageSize));
    const currentLinePage = Math.min(linePage, totalLinePages - 1);
    const paginatedLines = visibleLines.slice(
        currentLinePage * linePageSize,
        currentLinePage * linePageSize + linePageSize
    );

    useEffect(() => {
        setLinePage(0);
    }, [lineSearchText, linePageSize, lines.length]);


    const entrepotOptions = entrepots?.map(entrepot => ({
        value: entrepot.id,
        label: entrepot.code + ' - ' + entrepot.libelle,
    })) ?? [];

    const isEntrepotSelected = !!formik.values.entrepotId;

    const handleProductSelectedLegacy = async (product) => { // eslint-disable-line no-unused-vars

        const { data } = await stockService.getStock(product.id, formik.values.entrepotId);

        const existingIndex = formik.values.lignes.findIndex(
            l => l.articleId === product.id
        );

        if (existingIndex >= 0) {
            // Already exists → increase physical qty
            const updated = [...formik.values.lignes];
            updated[existingIndex].quantitePhysique =
                Number(updated[existingIndex].quantitePhysique) + 1;
            formik.setFieldValue("lignes", updated);
        } else {
            // New line
            formik.setFieldValue("lignes", [
                ...formik.values.lignes,
                {
                    articleId: product.id,
                    articleCode: product.code,
                    articleLibelle: product.libelle,
                    quantiteTheorique: data.quantiteDisponible || 0,
                    quantitePhysique: 1
                }
            ]);
        }

        setSearchText("");
    };

    const handleProductSelected = async (product) => {
        const existingIndex = lines.findIndex(l => l.articleId === product.id);

        if (existingIndex >= 0) {
            const updated = [...lines];
            const [existingLine] = updated.splice(existingIndex, 1);
            updated.unshift(existingLine);
            formik.setFieldValue("lignes", updated);

            setDuplicateArticleId(product.id);
            setHighlightedArticleId(product.id);
            setLinePage(0);
            setSearchText("");

            Swal.fire({
                icon: "warning",
                title: "Article deja ajoute",
                text: "Cet article existe deja dans l'inventaire.",
                timer: 1800,
                showConfirmButton: false
            });
            return;
        }

        const { data } = await stockService.getStock(product.id, formik.values.entrepotId);

        formik.setFieldValue("lignes", [
            {
                articleId: product.id,
                articleCode: product.code,
                articleLibelle: product.libelle,
                quantiteTheorique: data.quantiteDisponible || 0,
                quantitePhysique: 1
            },
            ...lines
        ]);

        setDuplicateArticleId(null);
        setHighlightedArticleId(product.id);
        setLinePage(0);
        setSearchText("");
    };

    const handleRemoveLine = (index) => {
        if (isLocked) return;

        const updated = [...lines];
        updated.splice(index, 1);
        formik.setFieldValue("lignes", updated);
    };

    const getLineRowClassName = (line) => {
        const matchesSearch = normalizedLineSearch &&
            `${line.articleCode || ""} ${line.articleLibelle || ""}`.toLowerCase().includes(normalizedLineSearch);

        if (line.articleId === duplicateArticleId) return "table-warning";
        if (matchesSearch) return "table-info";
        if (line.articleId === highlightedArticleId) return "table-success";
        return "";
    };

    return (
        <Container>
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaClipboardCheck className="me-2 text-primary" />
                                {id ? "Modifier inventaire" : "Nouvel inventaire"}
                            </h4>
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    color="secondary"
                                    outline
                                    onClick={() => navigate("/inventories")}
                                >
                                    <FaArrowLeft className="me-1" />
                                    Retour
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {isLocked && (
                <div className="alert alert-info mt-2">
                    Cet inventaire est validé et ne peut plus être modifié.
                </div>
            )}
            <Card>
                <CardBody>
                    <form onSubmit={formik.handleSubmit}>
                        <Row>
                            <Col md={4}>
                                <label>Date</label>
                                <Input
                                    type="date"
                                    name="dateInventaire"
                                    value={formik.values.dateInventaire}
                                    onChange={formik.handleChange}
                                    disabled={isLocked}
                                />
                            </Col>
                            <Col md={4}>
                                <label>Entrepôt</label>

                                <Select
                                    id="entrepotId"
                                    options={entrepotOptions}
                                    value={entrepotOptions.find(option => option.value === formik.values.entrepotId)}
                                    onChange={(selectedOption) => formik.setFieldValue('entrepotId', selectedOption.value)}
                                    placeholder="Sélectionner..."
                                    isSearchable
                                    isDisabled={isLocked}
                                />
                            </Col>
                        </Row>

                        <hr />
                        {!isEntrepotSelected && (
                            <div className="text-warning mb-2">
                                Sélectionnez d'abord un entrepôt pour commencer le scan.
                            </div>
                        )}
                        <Row className="mb-3 align-items-end">
                            <Col md={5}>
                                <Input
                                    placeholder="Scanner code barre ou rechercher article..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();   // 🛑 STOP FORM SUBMIT

                                            if (!searchText.trim()) return;

                                            dispatch(productActions.findAll({
                                                search: searchText,
                                                page: 0,
                                                size: 5,
                                                onSuccess: (results) => {
                                                    if (results.length === 1) {
                                                        handleProductSelected(results[0]);
                                                    } else if (results.length > 1) {
                                                        setModalProducts(results);
                                                        setModalOpen(true);
                                                    } else {
                                                        Swal.fire("Aucun article trouvé");
                                                    }
                                                }
                                            }));
                                        }
                                    }}
                                    disabled={!isEntrepotSelected}
                                />
                            </Col>
                            <Col md={5}>
                                <Input
                                    placeholder="Rechercher dans les lignes saisies..."
                                    value={lineSearchText}
                                    onChange={(e) => setLineSearchText(e.target.value)}
                                />
                            </Col>
                            <Col md={2}>
                                <Input
                                    type="select"
                                    value={linePageSize}
                                    onChange={(e) => setLinePageSize(Number(e.target.value))}
                                >
                                    <option value={5}>5 lignes</option>
                                    <option value={10}>10 lignes</option>
                                    <option value={20}>20 lignes</option>
                                    <option value={50}>50 lignes</option>
                                </Input>
                            </Col>
                        </Row>

                        <Table bordered>
                            <thead>
                                <tr>
                                    <th>Article</th>
                                    <th>Théorique</th>
                                    <th>Physique</th>
                                    <th>Écart</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedLines.map(({ line: l, index: i }) => (
                                    <tr key={`${l.articleId}-${i}`} className={getLineRowClassName(l)}>
                                        <td>{l.articleCode} - {l.articleLibelle}</td>
                                        <td>{l.quantiteTheorique}</td>
                                        <td>
                                            <Input
                                                type="number"
                                                name={`lignes.${i}.quantitePhysique`}
                                                value={l.quantitePhysique}
                                                onChange={formik.handleChange}
                                                onFocus={(e) => {
                                                    e.target.select();
                                                    e.target.addEventListener('wheel', preventScroll, { passive: false });
                                                }}
                                                disabled={isLocked}
                                            />
                                        </td>
                                        <td
                                            className={
                                                (l.quantiteTheorique < 0
                                                    ? l.quantitePhysique
                                                    : l.quantitePhysique - l.quantiteTheorique) !== 0
                                                    ? "text-danger fw-bold"
                                                    : ""
                                            }
                                        >
                                            {l.quantiteTheorique < 0
                                                ? l.quantitePhysique
                                                : l.quantitePhysique - l.quantiteTheorique}
                                        </td>
                                        <td>
                                            <Button
                                                type="button"
                                                color="danger"
                                                outline
                                                size="sm"
                                                onClick={() => handleRemoveLine(i)}
                                                disabled={isLocked}
                                            >
                                                Supprimer
                                            </Button>
                                        </td>

                                    </tr>
                                ))}
                                {paginatedLines.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="text-center text-muted">
                                            Aucune ligne trouvee
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>

                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <span className="text-muted">
                                {visibleLines.length} ligne(s) affichee(s) sur {lines.length}
                            </span>
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    type="button"
                                    color="secondary"
                                    outline
                                    size="sm"
                                    disabled={currentLinePage === 0}
                                    onClick={() => setLinePage(currentLinePage - 1)}
                                >
                                    Precedent
                                </Button>
                                <span>
                                    Page {currentLinePage + 1} / {totalLinePages}
                                </span>
                                <Button
                                    type="button"
                                    color="secondary"
                                    outline
                                    size="sm"
                                    disabled={currentLinePage >= totalLinePages - 1}
                                    onClick={() => setLinePage(currentLinePage + 1)}
                                >
                                    Suivant
                                </Button>
                            </div>
                        </div>

                        {!isLocked && (
                            <Button type="submit" color="success">Enregistrer</Button>
                        )}
                    </form>
                </CardBody>
            </Card>

            <ProductSelectModal
                isOpen={modalOpen}
                toggle={() => setModalOpen(false)}
                products={modalProducts}
                onSelect={handleProductSelected}
            />
        </Container>
    );
};

export default InventoryFormPage;
